import { useMemo } from 'react';
import { BeaconStudentListDocument, SortDirection } from '../../../../graphQL';
import type {
  BeaconStudentListQuery,
  BeaconStudentListQueryVariables,
  BeaconSurveyStatus,
  TriageCategory,
} from '../../../../graphQL';
import type {
  DeclaredFilters,
  SortState,
  UseListPaginationResult,
} from '../../../../Hooks/useList/types';
import { useListPagination } from '../../../../Hooks/useList/useListPagination';
import { beaconSortField, STUDENT_LIST_LOAD_ERROR, toBeaconSortField } from '../constants';
import { useGetSurveyOptions } from './useGetSurveyOptions';
import type { SurveyOption } from './useGetSurveyOptions';

export type BeaconStudentRow = BeaconStudentListQuery['beaconStudentList']['items'][number];

export type SurveyDropdownOption = { id: number; label: string };

// The survey is a filter like the others, so picking one returns the list to its first page.
// Unset means "the default survey" (below).
export type StudentListFilters = {
  surveyId?: number;
  surveyStatus?: BeaconSurveyStatus;
  triageCategory?: TriageCategory;
  hasComplexNeeds?: true;
};

type UseStudentListLogicResult =
  | { status: 'loading' }
  // `onRetry` is there when the student query failed; a failure loading the survey options has none.
  | { status: 'error'; errorMessage: string; onRetry?: () => void }
  | {
      status: 'success';
      surveys: SurveyDropdownOption[];
      selectedSurveyId: number | null;
      // False only when the selected survey predates student-search support (a V1 survey); the
      // view swaps the list for a not-compatible message. True when nothing is selected, so the
      // view renders an (empty) list rather than the message.
      isSelectedSupported: boolean;
      // Whether the org has any survey this list can render. False means nothing to default to or
      // pick, so the view shows the no-surveys copy rather than the "select a survey" prompt.
      hasSupportedSurveys: boolean;
      list: UseListPaginationResult<BeaconStudentRow, StudentListFilters>;
    };

// Stable empty reference so the derivations below don't re-run while options load.
const NO_OPTIONS: SurveyOption[] = [];

const INITIAL_FILTERS: DeclaredFilters<StudentListFilters> = {
  surveyId: undefined,
  surveyStatus: undefined,
  triageCategory: undefined,
  hasComplexNeeds: undefined,
};
const INITIAL_SORT: SortState = { field: beaconSortField.Name, direction: 'asc' };
const PAGE_SIZE = 10;

// The survey the list shows: the user's pick, else the default. An id absent from the current
// options resolves to none.
const resolveSurvey = (
  options: SurveyOption[],
  surveyId: number | undefined,
  defaultSurveyId: number | null
): SurveyOption | undefined => options.find(option => option.id === (surveyId ?? defaultSurveyId));

export const useStudentListLogic = (organizationId: number): UseStudentListLogicResult => {
  const optionsResult = useGetSurveyOptions(organizationId);
  const options = optionsResult.status === 'success' ? optionsResult.options : NO_OPTIONS;

  // The default lands the provider on a survey they can actually view
  const defaultSurveyId = useMemo(() => {
    const active = options.find(option => option.isActive);
    if (active?.isStudentSearchSupported) return active.id;
    return options.find(option => option.isStudentSearchSupported)?.id ?? null;
  }, [options]);
  const hasSupportedSurveys = defaultSurveyId !== null;

  const surveys = useMemo<SurveyDropdownOption[]>(
    () =>
      options.map(option => ({
        id: option.id,
        label: option.isActive ? `${option.name} (Active)` : option.name,
      })),
    [options]
  );

  const list = useListPagination<
    BeaconStudentListQuery,
    BeaconStudentListQueryVariables,
    BeaconStudentRow,
    StudentListFilters
  >({
    query: BeaconStudentListDocument,
    toVariables: ({ cursor, pageSize, sort, search, filters }) => ({
      params: {
        organizationId,
        // `skip` holds the query until a supported survey is selected, so the 0 is never sent.
        surveyId: resolveSurvey(options, filters.surveyId, defaultSurveyId)?.id ?? 0,
        cursor,
        search: search || undefined,
        surveyStatus: filters.surveyStatus,
        triageCategory: filters.triageCategory,
        hasComplexNeeds: filters.hasComplexNeeds,
        sortField: toBeaconSortField(sort?.field),
        sortDirection:
          sort?.direction === 'desc' ? SortDirection.Descending : SortDirection.Ascending,
        limit: pageSize,
      },
    }),
    selectPage: data => data.beaconStudentList,
    // No selection, or a V1 survey the view has swapped out: the list stays empty without querying.
    skip: ({ filters }) =>
      !resolveSurvey(options, filters.surveyId, defaultSurveyId)?.isStudentSearchSupported,
    initialFilters: INITIAL_FILTERS,
    initialSort: INITIAL_SORT,
    initialPageSize: PAGE_SIZE,
    // No `fetchPolicy`: the rows hold student names, and the cache outlives a logout (PDE-1761).
    // No `persist`: the search box holds student names, which must not land in the URL.
    analyticsName: 'beacon-student-list',
  });

  const selectedSurvey = resolveSurvey(options, list.filters.surveyId, defaultSurveyId);

  if (optionsResult.status === 'loading') {
    return { status: 'loading' };
  }
  if (optionsResult.status === 'error') {
    return { status: 'error', errorMessage: optionsResult.error };
  }
  // `refetch` asks for the same page again, keeping the provider's search and filters.
  if (list.error) {
    return { status: 'error', errorMessage: STUDENT_LIST_LOAD_ERROR, onRetry: list.refetch };
  }

  return {
    status: 'success',
    surveys,
    selectedSurveyId: selectedSurvey?.id ?? null,
    isSelectedSupported: selectedSurvey ? selectedSurvey.isStudentSearchSupported : true,
    hasSupportedSurveys,
    list,
  };
};
